import React, { useEffect, useState, useRef, useMemo, useCallback } from "react";
import { useSelector } from "react-redux";
import {
    Search,
    Filter,
    Trash2,
    Heart,
    Share2,
    MapPin,
    Bed,
    Bath,
    Car,
    Square,
    Calendar,
    ExternalLink,
    Plus,
    ChevronDown,
    ChevronUp,
    Grid,
    Grid3x3,
    List,
    Edit,
    Home,
    Building2,
    Trees,
    Tractor,
    Layers,
    TrendingUp,
    X,
    Circle,
    Check,
    Zap,
    Coins,
    Maximize2,
    Users
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import { updateCollingsProperty, deleteCollingsProperty, patchCollingsProperty } from "../../../api/collingsProperties";
import { deleteProperty, updateProperty, getProperties, patchProperty, getListingAgents } from "../../../api/agent";
import moment from "moment";
import toast from "react-hot-toast";
import { capitalizeEachWord } from "../../../helper/function";
import { formatShortAddress } from "../../../utils/addressFormatter";
import SkeletonPropertyCard from "../../../components/search/SkeletonPropertyCard";
import { cachedApiCall, cacheKeys, invalidateCache } from "../../../lib/cache";
import LazyImage from "../../../components/common/LazyImage";
import ManualSearchOverlay from "../components/ManualSearchOverlay";
import PropertySearchBar from "../components/PropertySearchBar";
import PropertyCard from "../components/PropertyCard";
import PropertyGridView from "../components/PropertyGridView";
import PropertyListView from "../components/PropertyListView";
import PropertySortDropdown from "../components/PropertySortDropdown";
import { DEFAULT_LIST_SORT, listSortParam } from "../utils/propertySort";
import PropertyFilterTabs from "../components/PropertyFilterTabs";
import { parsePropertySearch, parsePropertySearchAI } from "../utils/aiSearchParser";
import {
    COLLINGS_MARKET_RULES,
    DEFAULT_MARKET_TILES,
    DEFAULT_STATUS_TILES,
    STATUS_TILE_OPTIONS,
    matchesTiles,
    showsMarketRow,
} from "../utils/propertyTiles";
import { hasAdminAccess, hasSuperAdminAccess } from "../../../utils/roleAccess";

const notFoundImg =
    "https://atozpropertysolutionsltd.com/property-not-found.svg";

const PAGE_SIZE = 9;

const CollingsPropertiesPage = () => {
    const { user } = useSelector((state) => state.auth);

    const isAdmin = useMemo(() => hasAdminAccess(user), [user]);
    // Agents shouldn't see other agents' appraisals — hide the Appraisal tile for
    // everyone except admins, who need to review appraisals across all agents.
    const statusTileOptions = useMemo(
        () => isAdmin
            ? STATUS_TILE_OPTIONS
            : STATUS_TILE_OPTIONS.filter((option) => option.id !== "appraisal"),
        [isAdmin]
    );

    /*
     * Super admin agent switcher (Christian Gravias, Monday 13202768457: "Add a
     * feature for Super Admin to switch between individual agents"). Picking a
     * name narrows the page to that agent's listings - primary, co- or tertiary
     * agent - through the backend's ?agent= filter; "All agents" puts the whole
     * office back. Not remembered between visits: the page always opens on the
     * whole office, as it always has.
     */
    const isSuperAdmin = useMemo(() => hasSuperAdminAccess(user), [user]);
    const [agents, setAgents] = useState([]);
    const [viewingAgent, setViewingAgent] = useState(null); // { id, name }
    const [switcherOpen, setSwitcherOpen] = useState(false);
    const switcherRef = useRef(null);

    useEffect(() => {
        if (!isSuperAdmin) return;
        let cancelled = false;
        getListingAgents()
            .then((res) => {
                if (!cancelled) setAgents(Array.isArray(res.data?.agents) ? res.data.agents : []);
            })
            .catch(() => {
                // No list, no switcher - the page still works as the office view.
                if (!cancelled) setAgents([]);
            });
        return () => { cancelled = true; };
    }, [isSuperAdmin]);

    useEffect(() => {
        if (!switcherOpen) return;
        const close = (e) => {
            if (switcherRef.current && !switcherRef.current.contains(e.target)) {
                setSwitcherOpen(false);
            }
        };
        document.addEventListener("mousedown", close);
        return () => document.removeEventListener("mousedown", close);
    }, [switcherOpen]);

    const pickAgent = (agent) => {
        setViewingAgent(agent ? { id: agent.id, name: agent.name } : null);
        setSwitcherOpen(false);
    };

    const [viewMode, setViewMode] = useState("grid"); // 'grid' or 'list'
    // `inputValue` is what's typed in the box; `searchTerm` drives filtering,
    // AI parsing, and the backend fetch. `searchTerm` follows `inputValue` with
    // a short debounce so the list reacts live as the user types, while
    // Enter/Search still flushes immediately.
    const [inputValue, setInputValue] = useState("");
    const [searchTerm, setSearchTerm] = useState("");
    const submitSearch = useCallback(() => setSearchTerm(inputValue), [inputValue]);
    const [properties, setProperties] = useState([]);
    const [nextPage, setNextPage] = useState(null);
    const [totalCount, setTotalCount] = useState(0);
    const [loading, setLoading] = useState(true);
    const [loadingMore, setLoadingMore] = useState(false);
    const [error, setError] = useState(null);
    const [currentPage, setCurrentPage] = useState(1);
    const itemsPerPage = PAGE_SIZE;
    const queryFiltersRef = useRef({});
    const loadMoreSentinelRef = useRef(null);
    // Every filter / tile / sort change starts a new generation. Load-more waits
    // for that generation's page 1, and a reply from an older generation is
    // dropped. Without this, switching Market or Sort after scrolling fired a
    // stale "page 2" alongside the new page 1 and mixed the two lists together.
    const fetchGenRef = useRef(0);
    const pageOneGenRef = useRef(-1);
    const [deletingId, setDeletingId] = useState(null);
    const [editModalOpen, setEditModalOpen] = useState(false);
    const [editProperty, setEditProperty] = useState(null);
    const navigate = useNavigate();
    const searchTimeout = useRef();
    const [imageIndexes, setImageIndexes] = useState({});
    const [isUpdating, setIsUpdating] = useState(false);

    // Filter states
    const [showFilters, setShowFilters] = useState(false);
    const [filters, setFilters] = useState({
        listingType: 'all', // 'all', 'public', 'offmarket'
        suburb: '',
        minBeds: '',
        minBaths: '',
        minCarSpaces: '',
        priceMin: '',
        priceMax: ''
    });

    // New state for UI revamp
    // Status row (All / For Sale / For Lease / Appraisal / Sold / Leased /
    // Withdrawn) and the Market row under it. Opens on For Sale, as before.
    const [selectedStatuses, setSelectedStatuses] = useState(DEFAULT_STATUS_TILES);
    const [selectedMarkets, setSelectedMarkets] = useState(DEFAULT_MARKET_TILES);
    const [sortConfig, setSortConfig] = useState({ key: null, direction: "asc" });
    // The Sort dropdown above the search bar. The backend orders the list, so
    // every page that loads follows on from the last (utils/propertySort.js).
    const [listSort, setListSort] = useState(DEFAULT_LIST_SORT);
    // A pick from the dropdown takes over from any column-header sort, which
    // only re-orders the rows already loaded.
    const handleListSortChange = (next) => {
        setListSort(next);
        setSortConfig({ key: null, direction: "asc" });
    };

    // Advanced Filter State
    const [buyerType, setBuyerType] = useState("owner");
    const [advFilters, setAdvFilters] = useState({
        propertyTypes: ["all"],
        priceRange: { min: "Any", max: "Any" },
        bedrooms: { min: "Any", max: "Any" },
        useBedroomRange: false,
        bathrooms: "Any",
        carSpaces: "Any",
        landSize: { min: "Any", max: "Any" },
        saleMethod: "All",
        excludeUnderContract: false,
        targetYield: "Any",
        capitalGrowth: "Any",
        showWithPriceOnly: false,
    });

    const handleToggleFilter = (current, setFunction, value) => {
        if (current.includes(value)) {
            const next = current.filter(v => v !== value);
            if (next.length === 0) {
                setFunction(["all"]);
            } else {
                setFunction(next);
            }
        } else {
            setFunction([...current, value]);
        }
    };

    const getDaysBadgeColorLocal = (days) => {
        if (days <= 14) return "bg-[#00D094] text-white";
        if (days <= 30) return "bg-[#FF8E26] text-white";
        return "bg-[#EE3E34] text-white";
    };

    const getBorderColorLocal = (days) => {
        if (days <= 14) return "border-[#00D094]";
        if (days <= 30) return "border-[#FF8E26]";
        return "border-[#EE3E34]";
    };

    const handleSort = (key, explicitDirection = null) => {
        let direction = explicitDirection;
        if (!direction) {
            direction = "asc";
            if (sortConfig.key === key && sortConfig.direction === "asc") {
                direction = "desc";
            }
        }
        setSortConfig({ key, direction });
    };

    const transformResults = (rawItems, isCrmProperty) => {
        return rawItems.map(item => {
            const address = formatShortAddress(item);

            const features = item.features || {};
            const images = item.extra_info?.images?.map(url => ({ file: url })) ||
                          item.images?.map(img => ({ file: img.file || img })) || [];

            // Route based on the source list this item came from. The CRM
            // serializer aliases street_name as `street`, so heuristics on field
            // shape misclassify CRM rows as REA and 404 the detail page.
            const detailPath = isCrmProperty ? `/properties/${item.id}` : `/collings-properties/${item.id}`;

            return {
                ...item,
                is_crm_property: isCrmProperty,
                detailPath: detailPath,
                address: address || item.headline || item.title || "No Address",
                price: item.price,
                title: item.headline || item.title,
                beds: item.beds || features.bedrooms || 0,
                baths: item.baths || features.bathrooms || 0,
                parkings: item.parkings || (parseInt(features.garages || 0) + parseInt(features.carports || 0) + parseInt(features.openSpaces || 0)) || 0,
                images: images,
                property_type: item.property_type || item.category || item.listing_type,
                created_at: item.created_at,
                est_gross_yield: item.est_gross_yield || item.extra_info?.est_gross_yield || null,
                est_outgoings: item.est_outgoings || item.extra_info?.est_outgoings || null,
                est_net_yield: item.est_net_yield || item.extra_info?.est_net_yield || null,
                commission_type: item.commission_type || item.extra_info?.commission_type || null,
                commission_rate: item.commission_rate || item.extra_info?.commission_rate || null,
                commission_amount: item.commission_amount || item.extra_info?.commission_amount || null
            };
        });
    };

    const fetchProperties = async (page = 1, filters = {}, options = {}) => {
        const { append = false } = options;
        const gen = fetchGenRef.current;
        if (!append) {
            setLoading(true);
            setError(null);
        } else {
            setLoadingMore(true);
        }
        try {
            const params = {
                page,
                page_size: itemsPerPage,
                ...filters
            };

            // Single source of truth: the CRM property_management_property table.
            // Every Collings property has a CRM Property (created on upload and kept in
            // sync by the backend's sync_rea_to_property). We no longer merge in the REA
            // mirror records — doing so produced duplicate cards whenever the REA<->CRM
            // link was missing (one REA card + one CRM card for the same property).
            const crmParams = { ...params, agency_wide: true };

            const crmRes = await cachedApiCall(
                `collings-crm-${JSON.stringify(crmParams)}`,
                () => getProperties(crmParams),
                3 * 60 * 1000
            );

            if (gen !== fetchGenRef.current) return;
            if (!append) pageOneGenRef.current = gen;
            const crmData = crmRes.data;
            const rawCrmItems = Array.isArray(crmData?.results) ? crmData.results : [];

            // Filter out Buyer Portal appraisal leads from CRM properties
            // These are leads submitted via the "Sell Your Property" form and should NOT appear in
            // Collings Properties, except on the Appraisal tab where those leads ARE appraisals.
            const filteredCrmItems = rawCrmItems.filter(item => {
                if (selectedStatuses.includes("appraisal")) return true;
                const description = (item.description || '').toLowerCase();
                const internalNotes = (item.internal_notes || '').toLowerCase();
                const isBuyerPortalLead = description.includes('buyer portal') || internalNotes.includes('buyer portal');
                return !isBuyerPortalLead;
            });

            const combinedItems = transformResults(filteredCrmItems, true);

            if (append) {
                setProperties(prev => [...prev, ...combinedItems]);
            } else {
                setProperties(combinedItems);
            }

            setNextPage(crmData?.next || null);
            setCurrentPage(page);
            setTotalCount(crmData?.count || 0);
        } catch (err) {
            console.error(err);
            setError("Failed to load properties");
            toast.error("Failed to load properties");
        } finally {
            // A dropped (older) page 1 leaves the spinner to the newer one.
            if (append) setLoadingMore(false);
            else if (gen === fetchGenRef.current) setLoading(false);
        }
    };

    const loadMore = useCallback(() => {
        if (loadingMore || loading || !nextPage) return;
        if (pageOneGenRef.current !== fetchGenRef.current) return;
        const nextPageNum = currentPage + 1;
        fetchProperties(nextPageNum, queryFiltersRef.current, { append: true });
    }, [currentPage, nextPage, loadingMore, loading]);

    // Instant local parse so the list reacts on every keystroke.
    const localParsedSearch = useMemo(
        () => parsePropertySearch(searchTerm),
        [searchTerm]
    );

    // Claude-parsed filters (debounced). Falls back to local parse on failure.
    // We keep both so the UI never blocks waiting for the API call.
    const [aiParsedSearch, setAiParsedSearch] = useState(null);
    useEffect(() => {
        const term = (searchTerm || "").trim();
        if (!term) {
            setAiParsedSearch(null);
            return;
        }
        // Skip the AI roundtrip for short or purely-numeric queries — those are
        // address fragments (e.g. "202"), not natural-language intents, and the
        // backend Claude call adds latency without helping.
        const isShortOrNumeric = term.length < 5 || /^[\d\s/.-]+$/.test(term);
        if (isShortOrNumeric) {
            setAiParsedSearch(null);
            return;
        }
        const t = setTimeout(() => {
            parsePropertySearchAI(term).then((parsed) => {
                // Guard against stale resolutions if searchTerm changed since.
                setAiParsedSearch((prev) => {
                    if (term !== (searchTerm || "").trim()) return prev;
                    return parsed;
                });
            });
        }, 350);
        return () => clearTimeout(t);
    }, [searchTerm]);

    const parsedSearch = aiParsedSearch || localParsedSearch;

    // Whether the current query carries AI/structured intent (beds, price,
    // category, suburb, features, or residual keywords). When true the BACKEND
    // does the filtering (layered address-then-criteria search), so the client
    // must NOT re-strip the results — that re-stripping (esp. the literal whole
    // -sentence substring guarantee) is what produced "0 of N" + the infinite
    // "Loading more…" loop.
    const aiActive = useMemo(() => {
        const ai = parsedSearch?.filters || {};
        const hasFilter = Object.keys(ai).some(
            (k) => k !== "features" && ai[k] != null && ai[k] !== ""
        );
        const hasFeatures = Array.isArray(ai.features) && ai.features.length > 0;
        const hasKeywords = !!(parsedSearch?.keywords && parsedSearch.keywords.trim());
        return hasFilter || hasFeatures || hasKeywords;
    }, [parsedSearch]);

    // A search that names its own status or listing type ("sold in Preston",
    // "houses for lease") is the source of truth over the tiles, as it always
    // was. ANDing it with the lit tiles instead would hand back zero rows
    // whenever the two disagree, e.g. "sold" typed while For Sale is lit.
    const aiOverridesTiles = useMemo(() => {
        const ai = parsedSearch?.filters || {};
        return aiActive && (!!ai.status || ai.listingType === "sales" || ai.listingType === "lease");
    }, [aiActive, parsedSearch]);

    const filteredProperties = useMemo(() => {
        let result = [...properties];

        // When the query has AI/structured intent the backend already did the
        // (layered) filtering, so we trust its results verbatim. Re-applying the
        // client-side filters here — especially the whole-sentence substring
        // guarantee below — would strip every backend match to "0 of N".
        //
        // For a plain non-AI text query (e.g. a bare "202") the backend has no
        // structured criteria to act on, so we keep the substring guarantee to
        // refine its broad full-text match.
        if (!aiActive && searchTerm && searchTerm.trim()) {
            const raw = searchTerm.trim().toLowerCase();
            result = result.filter((p) => {
                const hay = [p.address, p.suburb, p.title, p.headline]
                    .filter(Boolean)
                    .join(" ")
                    .toLowerCase();
                return hay.includes(raw);
            });
        }

        // Status / Market tiles. One shared rule (matchesTiles) decides which
        // tile a property belongs to, the same rule the backend `tiles` param
        // applies, so this pass only ever trims rows the backend already agreed
        // on. Withdrawn listings sit in the Withdrawn tile alone: a withdrawn
        // on-market listing keeps its "on market" status name, which is how one
        // was turning up under For Sale (Christian Gravias, 1 Oct 2026).
        if (!aiOverridesTiles) {
            result = result.filter(p => matchesTiles(p, {
                statuses: selectedStatuses,
                markets: selectedMarkets,
                searching: Boolean(searchTerm.trim()),
                marketRules: COLLINGS_MARKET_RULES,
            }));
        }

        // Appraisals live in the pipeline, so they are dropped from every tab
        // except Appraisal - and from nothing at all while a search is running,
        // which is how they stay findable (Simon Abbott, 3 Sep 2026).
        if (!selectedStatuses.includes("appraisal") && !searchTerm.trim()) {
            result = result.filter(p => {
                const statusName = (
                    p?.status?.name || p?.status || p?.extra_info?.status || ""
                ).toString().toLowerCase();
                return !statusName.includes("appraisal");
            });
        }

        // Sorting
        if (sortConfig.key) {
            result.sort((a, b) => {
                let aValue = a[sortConfig.key];
                let bValue = b[sortConfig.key];

                // Map sort keys to actual property keys if needed
                if (sortConfig.key === 'parking') {
                    aValue = a.parkings || a.parking;
                    bValue = b.parkings || b.parking;
                } else if (sortConfig.key === 'type') {
                    aValue = a.property_type || a.type;
                    bValue = b.property_type || b.type;
                } else if (sortConfig.key === 'area') {
                    aValue = a.floor_area || a.area || a.building_area;
                    bValue = b.floor_area || b.area || b.building_area;
                } else if (sortConfig.key === 'gross_yield') {
                    aValue = a.gross_yield || a.grossYield || a.est_gross_yield;
                    bValue = b.gross_yield || b.grossYield || b.est_gross_yield;
                } else if (sortConfig.key === 'net_yield') {
                    aValue = a.net_yield || a.netYield || a.est_net_yield;
                    bValue = b.net_yield || b.netYield || b.est_net_yield;
                }

                // Handle numeric string conversions
                const numericKeys = ['price', 'beds', 'baths', 'parkings', 'area', 'land_area', 'rent', 'gross_yield', 'net_yield'];
                if (numericKeys.includes(sortConfig.key) || sortConfig.key === 'parking' || sortConfig.key === 'floor_area') {
                    const parseVal = (val) => {
                        if (typeof val === 'number') return val;
                        if (!val) return 0;
                        if (typeof val === 'string') {
                            return parseFloat(val.replace(/[^0-9.-]+/g, '')) || 0;
                        }
                        return 0;
                    };
                    aValue = parseVal(aValue);
                    bValue = parseVal(bValue);
                }

                if ((aValue || 0) > (bValue || 0)) return sortConfig.direction === "asc" ? 1 : -1;
                if ((aValue || 0) < (bValue || 0)) return sortConfig.direction === "asc" ? -1 : 1;
                return 0;
            });
        }

        return result;
    }, [properties, sortConfig, searchTerm, aiActive, aiOverridesTiles, selectedStatuses, selectedMarkets]);

    const paginatedProperties = filteredProperties;

    // Build query filters from current filter state (used for initial fetch and load-more)
    const buildQueryFilters = useCallback(() => {
        const queryFilters = {};
        const rawTerm = searchTerm.trim();
        const ai = parsedSearch?.filters || {};

        // Layered search is server-side now (PropertyListingView):
        //   ai_search = the raw query -> the backend tries an ADDRESS match first
        //   structured params below -> the fallback "search the DB by criteria".
        // We hand over the raw text + parsed criteria and let the DB filter and
        // count. (No more AND-joined `search` that matched nothing, and no
        // client-side page-stripping that dead-ended pagination.)
        if (aiActive) {
            if (rawTerm) queryFilters.ai_search = rawTerm;
        } else if (rawTerm) {
            queryFilters.search = rawTerm;
        }

        // Beds: "2 bedroom" parses to exactBeds (NOT minBeds); exact -> min==max.
        if (ai.exactBeds != null) {
            queryFilters.min_beds = ai.exactBeds;
            queryFilters.max_beds = ai.exactBeds;
        } else {
            if (ai.minBeds != null) queryFilters.min_beds = ai.minBeds;
            if (ai.maxBeds != null) queryFilters.max_beds = ai.maxBeds;
        }
        if (ai.exactBaths != null) {
            queryFilters.min_baths = ai.exactBaths;
            queryFilters.max_baths = ai.exactBaths;
        } else {
            if (ai.minBaths != null) queryFilters.min_baths = ai.minBaths;
            if (ai.maxBaths != null) queryFilters.max_baths = ai.maxBaths;
        }
        // Backend has no exact garages param, so exact collapses to a floor.
        if (ai.exactCarSpaces != null) queryFilters.min_car_spaces = ai.exactCarSpaces;
        else if (ai.minCarSpaces != null) queryFilters.min_car_spaces = ai.minCarSpaces;
        if (ai.minPrice != null) queryFilters.min_price = ai.minPrice;
        if (ai.maxPrice != null) queryFilters.max_price = ai.maxPrice;
        if (ai.suburb) queryFilters.suburb = ai.suburb;
        if (ai.category) queryFilters.category = ai.category;
        if (Array.isArray(ai.features) && ai.features.length) {
            queryFilters.features = ai.features.join(",");
        }

        // Sale state the AI extracted ("not sold", "sold", "off market", …).
        // These map to backend params the DB filters on, so e.g. "not sold" no
        // longer leaks Sold cards into the results.
        if (ai.excludeSold) queryFilters.exclude_sold = "true";
        if (ai.status === "sold") queryFilters.sold = "true";
        else if (ai.status === "withdrawn") queryFilters.withdrawn = "true";
        else if (ai.status === "off_market") queryFilters.method = "off_market";
        else if (ai.status === "on_market") queryFilters.method = "on_market";

        if (filters.suburb.trim()) queryFilters.suburb = filters.suburb.trim();
        if (advFilters.priceRange.min !== "Any") queryFilters.min_price = advFilters.priceRange.min;
        if (advFilters.priceRange.max !== "Any") queryFilters.max_price = advFilters.priceRange.max;
        if (advFilters.bedrooms.min !== "Any") queryFilters.min_beds = advFilters.bedrooms.min;
        if (advFilters.bedrooms.max !== "Any") queryFilters.max_beds = advFilters.bedrooms.max;
        if (advFilters.bathrooms !== "Any") queryFilters.min_baths = advFilters.bathrooms;
        if (advFilters.carSpaces !== "Any") queryFilters.min_car_spaces = advFilters.carSpaces;

        let methodFilter = null;
        let methodOfSaleFilter = null;
        if (advFilters.saleMethod && advFilters.saleMethod !== "All") {
            if (advFilters.saleMethod === "On Market") methodFilter = "on_market";
            else if (advFilters.saleMethod === "Off Market") methodFilter = "off_market";
            else if (advFilters.saleMethod === "Appraisal") methodFilter = "appraisal";
            else if (advFilters.saleMethod === "Private Sale") methodOfSaleFilter = "Private Sale";
            else if (advFilters.saleMethod === "Auction") methodOfSaleFilter = "Auction";
        }
        if (methodFilter) queryFilters.method = methodFilter;
        if (methodOfSaleFilter) queryFilters.method_of_sale = methodOfSaleFilter;

        // The lit tiles, for the backend to OR together (see matchesTiles).
        // Left off when "All" is lit during a search, so a sold or withdrawn
        // property can still be found by its address, and when the search
        // itself names a status or listing type.
        const searchingAll = selectedStatuses.includes("all") && Boolean(rawTerm);
        if (!aiOverridesTiles && !searchingAll) {
            queryFilters.tiles = selectedStatuses.join(",");
            if (showsMarketRow(selectedStatuses) && !selectedMarkets.includes("all")) {
                queryFilters.market = selectedMarkets.join(",");
            }
        }

        // Hide Sold from the backend count+results by default — both when browsing
        // (no search) AND when the query carries structured criteria intent
        // (beds/baths/price/type/"for sale"). A criteria search like
        // "4 bedroom properties for sale" means *available* listings, not sold
        // comps, so leaving Sold in (the old `!rawTerm`-only guard) was wrong.
        // Pure address/keyword lookups still return Sold so an agent can find a
        // specific sold property by typing its address. We never hide Sold when
        // the user explicitly wants it (Sold tile, or the query parsed to sold).
        // Leased counts as "wants completed rows" here as well as Sold - a leased
        // rental is stored with is_sold set, so exclude_sold would empty its tile.
        const onCompletedTab = selectedStatuses.includes("sold") || selectedStatuses.includes("leased");
        const wantsSold = onCompletedTab || ai.status === "sold";
        const hasCriteriaIntent =
            ai.exactBeds != null || ai.minBeds != null || ai.maxBeds != null ||
            ai.exactBaths != null || ai.minBaths != null || ai.maxBaths != null ||
            ai.exactCarSpaces != null || ai.minCarSpaces != null ||
            ai.minPrice != null || ai.maxPrice != null ||
            !!ai.category || ai.listingType === "sales" || ai.listingType === "lease" ||
            (Array.isArray(ai.features) && ai.features.length > 0);
        if (!wantsSold && (!rawTerm || hasCriteriaIntent)) {
            queryFilters.exclude_sold = "true";
        }

        // Same idea for For Sale / For Lease: the AI's listingType wins when it
        // actually detects one (e.g. "houses for lease in Preston"). Otherwise
        // For Sale / For Lease come from the `tiles` param above, which keeps a
        // rental out of For Sale even on a plain keyword search.
        if (ai.listingType === "sales" || ai.listingType === "lease") {
            queryFilters.listing_type = ai.listingType;
        }
        if (!advFilters.propertyTypes.includes("all")) {
            queryFilters.category = advFilters.propertyTypes[0];
        }

        // Appraisal-status properties are excluded from the default queryset
        // server-side, and stay excluded here unless the Appraisal tab is open
        // or the agent is searching. Simon Abbott, 3 Sep 2026: "Appraisals
        // should not go into Properties. Make them searchable but only have
        // them sit in pipeline so they don't clock up the Properties page."
        // Same rule as My Listings.
        if (selectedStatuses.includes("appraisal") || searchTerm.trim()) {
            queryFilters.include_appraisals = "true";
        }

        // The super admin's agent switcher: one agent's listings out of the
        // office's (primary, co- or tertiary agent).
        if (viewingAgent?.id) {
            queryFilters.agent = viewingAgent.id;
        }

        Object.assign(queryFilters, listSortParam(listSort));

        return queryFilters;
    }, [searchTerm, parsedSearch, aiActive, aiOverridesTiles, filters.suburb, advFilters, selectedStatuses, selectedMarkets, viewingAgent, listSort]);

    // On filter/search change: reset list and fetch first page (infinite scroll initial load)
    useEffect(() => {
        setCurrentPage(1);
        setProperties([]);
        // Clear the stale `next` URL too. Otherwise, during the gap between this
        // reset and the new page-1 response landing, the auto-advance effect sees
        // (0 visible rows + a leftover nextPage) and fires loadMore against the
        // OLD filters — the load/show/load/show flicker.
        setNextPage(null);
        // Flip loading immediately so the empty-state ("No properties found")
        // doesn't flash during the debounce window — we show skeletons instead.
        setLoading(true);
        fetchGenRef.current += 1;
        const queryFilters = buildQueryFilters();
        queryFiltersRef.current = queryFilters;
        if (searchTimeout.current) clearTimeout(searchTimeout.current);
        searchTimeout.current = setTimeout(() => {
            fetchProperties(1, queryFilters);
        }, 180);
        return () => {
            if (searchTimeout.current) clearTimeout(searchTimeout.current);
        };
    }, [searchTerm, advFilters, filters, buyerType, selectedStatuses, selectedMarkets, buildQueryFilters]);

    // Debounced live search: as the user types, sync `inputValue` -> `searchTerm`
    // so the list filters and refetches without requiring Enter / Search click.
    // The AI parser already debounces off `searchTerm`, so it keeps working too.
    useEffect(() => {
        if (inputValue === searchTerm) return;
        const t = setTimeout(() => {
            setSearchTerm(inputValue);
        }, 150);
        return () => clearTimeout(t);
    }, [inputValue, searchTerm]);

    // Auto-advance through a page whose rows were ALL removed by the remaining
    // client-side tab strips (e.g. an off-market REA leak on the On Market tab).
    // Guard with properties.length > 0: only skip forward when we actually loaded
    // rows that got fully stripped — never on the transient empty state between a
    // filter change and its first response (that caused the flickering loop).
    useEffect(() => {
        if (loading || loadingMore || !nextPage) return;
        if (properties.length === 0) return;
        if (filteredProperties.length > 0) return;
        loadMore();
    }, [loading, loadingMore, nextPage, properties.length, filteredProperties.length, loadMore]);

    // Infinite scroll: when sentinel is visible, load next page
    useEffect(() => {
        const sentinel = loadMoreSentinelRef.current;
        if (!sentinel || !nextPage) return;
        const observer = new IntersectionObserver(
            (entries) => {
                const [entry] = entries;
                if (entry?.isIntersecting && nextPage && !loadingMore && !loading) {
                    loadMore();
                }
            },
            { root: null, rootMargin: "200px", threshold: 0.1 }
        );
        observer.observe(sentinel);
        return () => observer.disconnect();
    }, [nextPage, loadingMore, loading, loadMore]);

    const handleClearFilters = () => {
        setFilters({
            listingType: 'all',
            suburb: '',
            minBeds: '',
            minBaths: '',
            minCarSpaces: '',
            priceMin: '',
            priceMax: ''
        });
        setAdvFilters({
            propertyTypes: ["all"],
            priceRange: { min: "Any", max: "Any" },
            bedrooms: { min: "Any", max: "Any" },
            useBedroomRange: false,
            bathrooms: "Any",
            carSpaces: "Any",
            landSize: { min: "Any", max: "Any" },
            saleMethod: "All",
            excludeUnderContract: false,
            targetYield: "Any",
            capitalGrowth: "Any",
            showWithPriceOnly: false,
        });
        setInputValue("");
        setSearchTerm("");
        setSelectedStatuses(DEFAULT_STATUS_TILES);
        setSelectedMarkets(DEFAULT_MARKET_TILES);
        setCurrentPage(1);
    };

    // A CRM Property row can only be opened in the Collings/REA editor when it has
    // a genuine REA link: the editor resolves crm-{id} → RealEstateProperty via
    // rea_listing_id server-side and 404s when there's no linked REA row. That's
    // the case for a blank rea_listing_id AND for the "REA_<pk>" published-marker
    // format (which matches no RealEstateProperty.listing_id). Route those to the
    // standard CRM property editor, which loads by Property id and always works.
    const hasResolvableReaLink = (property) => {
        const rid = property?.rea_listing_id;
        return !!rid && !String(rid).startsWith("REA_");
    };

    const handleEdit = (property) => {
        // Cards now come from the CRM Property table, so property.id is a Property id.
        if (property.is_crm_property && !hasResolvableReaLink(property)) {
            navigate(`/properties/edit/${property.id}`);
            return;
        }
        const editId = property.is_crm_property ? `crm-${property.id}` : property.id;
        navigate(`/collings-properties/edit/${editId}`);
    };

    const handleStageUpdate = async (propertyId, newStage) => {
        try {
            const target = properties.find(p => p.id === propertyId);
            // Same split as handleEdit. A CRM Property with no resolvable REA link
            // can't go through the Collings PATCH (crm-{id} → RealEstateProperty
            // resolution 404s); its status lives on the CRM Property itself, so
            // update that directly. Linked rows still PATCH the REA property, which
            // syncs the status back to the CRM Property.
            if (target?.is_crm_property && !hasResolvableReaLink(target)) {
                await patchProperty(propertyId, { status: newStage });
            } else {
                const apiId = target?.is_crm_property ? `crm-${propertyId}` : propertyId;
                await patchCollingsProperty(apiId, { status: newStage });
            }
            toast.success("Stage updated successfully");
            setProperties(prev => prev.map(p =>
                p.id === propertyId ? { ...p, status: newStage } : p
            ));
            // Invalidate cache prefix
            invalidateCache(`collings-properties-`);
        } catch (err) {
            toast.error("Failed to update stage");
        }
    };

    const getDaysOnMarket = (createdAt) => {
        if (!createdAt) return 0;
        const created = moment(createdAt);
        const now = moment();
        return now.diff(created, 'days');
    };

    return (
        <div className="min-h-screen bg-[#FFFFFF] p-0 sm:p-0 text-[#36423F] -mx-[14px] -my-3 sm:-mx-4 sm:-my-4 md:-m-6 w-auto max-w-full overflow-x-hidden">
            <div className="bg-[#F8F8F8] px-4 sm:px-6 md:px-6 lg:px-8 py-4 sm:py-5 md:py-4 lg:py-5 border-b border-gray-100">
                <div className="flex flex-col gap-3 sm:gap-4 md:gap-3 lg:gap-4">
                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 sm:gap-4">
                        <div className="flex items-center gap-2 flex-wrap min-w-0">
                            <h1 className="text-2xl sm:text-3xl font-bold text-[#55C5D0]">
                                {viewingAgent ? `${viewingAgent.name}'s Properties` : "Collings Properties"}
                            </h1>
                            {/* Super admin only, and only once there is somebody to
                                switch to (Christian Gravias, Monday 13202768457). */}
                            {isSuperAdmin && agents.length > 0 && (
                                <div className="relative" ref={switcherRef}>
                                    <button
                                        type="button"
                                        onClick={() => setSwitcherOpen((o) => !o)}
                                        title="Switch between agents"
                                        aria-haspopup="true"
                                        aria-expanded={switcherOpen}
                                        className="px-3 py-1.5 rounded-lg border border-[#E6E7E9] bg-white text-xs font-bold text-[#353B48] flex items-center gap-1.5"
                                    >
                                        <Users size={13} className="text-[#55C5D0]" />
                                        {viewingAgent ? viewingAgent.name : "All agents"}
                                        <ChevronDown size={13} className="text-[#9CA3AF]" />
                                    </button>
                                    {switcherOpen && (
                                        <div className="absolute left-0 mt-1 w-72 max-w-[calc(100vw-2rem)] bg-white border border-[#E6E7E9] rounded-xl shadow-xl z-40 py-1 max-h-80 overflow-y-auto custom-scrollbar">
                                            <button
                                                type="button"
                                                onClick={() => pickAgent(null)}
                                                className={`w-full text-left px-3 py-2 text-sm hover:bg-gray-50 ${
                                                    viewingAgent ? "text-[#353B48]" : "font-bold text-[#0e7d86]"
                                                }`}
                                            >
                                                All agents
                                            </button>
                                            <div className="border-t border-gray-100 my-1" />
                                            <p className="px-3 py-1 text-[10px] uppercase tracking-wide text-[#9CA3AF] font-bold">
                                                Agent · listings
                                            </p>
                                            {agents.map((agent) => (
                                                <button
                                                    key={agent.id}
                                                    type="button"
                                                    onClick={() => pickAgent(agent)}
                                                    className={`w-full text-left px-3 py-2 text-sm hover:bg-gray-50 flex items-center justify-between gap-2 ${
                                                        String(viewingAgent?.id) === String(agent.id)
                                                            ? "font-bold text-[#0e7d86]"
                                                            : "text-[#353B48]"
                                                    }`}
                                                >
                                                    <span className="truncate">{agent.name}</span>
                                                    <span className="text-[10px] text-[#9CA3AF] shrink-0">
                                                        {agent.count}
                                                    </span>
                                                </button>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>

                        <div className="flex flex-wrap items-center gap-2 sm:gap-4">
                            <div className="bg-[#EFEFEF] p-1 rounded-full flex items-center h-10 sm:h-12">
                                <button
                                    onClick={() => setViewMode("list")}
                                    className={`flex items-center gap-1 sm:gap-2 px-3 sm:px-6 py-2 rounded-full transition-all text-xs sm:text-sm ${viewMode === "list" ? "bg-white text-gray-900 shadow-sm" : "text-gray-500 hover:text-gray-700"}`}
                                >
                                    <List className="w-3 h-3 sm:w-4 sm:h-4" />
                                    <span className="font-bold">List</span>
                                </button>
                                <button
                                    onClick={() => setViewMode("grid")}
                                    className={`flex items-center gap-1 sm:gap-2 px-3 sm:px-6 py-2 rounded-full transition-all text-xs sm:text-sm ${viewMode === "grid" ? "bg-white text-gray-900 shadow-sm" : "text-gray-500 hover:text-gray-700"}`}
                                >
                                    <Grid3x3 className="w-3 h-3 sm:w-4 sm:h-4" />
                                    <span className="font-bold">Grid</span>
                                </button>
                            </div>

                            <button
                                onClick={() => navigate('/collings-properties/map')}
                                className="bg-white border border-gray-200 hover:border-[#55C5D0] hover:text-[#55C5D0] text-[#36423F] h-10 sm:h-12 px-4 sm:px-6 rounded-full font-bold shadow-sm transition-all flex items-center gap-2 active:scale-95 whitespace-nowrap text-xs sm:text-sm"
                            >
                                <MapPin className="w-4 h-4 sm:w-5 sm:h-5" />
                                <span>Map</span>
                            </button>

                            <button
                                onClick={() => navigate('/collings-properties/add')}
                                className="bg-[#36423F] hover:bg-[#2d3a37] text-white h-10 sm:h-12 px-4 sm:px-8 rounded-full font-bold shadow-lg transition-all flex items-center gap-2 active:scale-95 whitespace-nowrap text-xs sm:text-sm"
                            >
                                <Plus className="w-4 h-4 sm:w-5 sm:h-5" />
                                <span>Add Property</span>
                            </button>
                        </div>
                    </div>

                    <PropertyFilterTabs
                        selectedStatuses={selectedStatuses}
                        setSelectedStatuses={setSelectedStatuses}
                        selectedMarkets={selectedMarkets}
                        setSelectedMarkets={setSelectedMarkets}
                        statusOptions={statusTileOptions}
                    />
                </div>
            </div>

            <div className="px-4 sm:px-6 py-4 sm:py-6 md:py-5 pb-20 lg:pb-24 w-full overflow-x-hidden">
                <div className="relative mb-10">
                    {/* Sort sits on top of the search bar and settings, on every
                        screen size (Christian Gravias, 7 Oct 2026). */}
                    <div className="flex justify-end mb-3">
                        <PropertySortDropdown value={listSort} onChange={handleListSortChange} />
                    </div>
                    <PropertySearchBar
                        value={inputValue}
                        onChange={(e) => setInputValue(e.target.value)}
                        onSearchClick={submitSearch}
                        onFilterClick={() => setShowFilters(!showFilters)}
                        showFilters={showFilters}
                        placeholder="Search Address or Property Features with AI"
                        placeholderMobile="Search address or features"
                        searchButtonLabel="Search"
                    />
                    <ManualSearchOverlay
                        showFilters={showFilters}
                        setShowFilters={setShowFilters}
                        buyerType={buyerType}
                        setBuyerType={setBuyerType}
                        advFilters={advFilters}
                        setAdvFilters={setAdvFilters}
                        filters={filters}
                        setFilters={setFilters}
                        onClear={handleClearFilters}
                    />
                </div>

                <div className="mb-10">
                    {loading && !error ? (
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                            {[1, 2, 3, 4, 5, 6].map((i) => (
                                <div key={i} className="bg-white rounded-xl border border-gray-100 p-6 animate-pulse">
                                    <div className="h-48 bg-gray-100 rounded-lg mb-4"></div>
                                    <div className="h-4 bg-gray-100 rounded w-3/4 mb-2"></div>
                                    <div className="h-3 bg-gray-100 rounded w-1/2 mb-4"></div>
                                </div>
                            ))}
                        </div>
                    ) : error ? (
                        <div className="text-center py-16">
                            <div className="text-red-500 mb-2">Error loading properties ({error})</div>
                            <button onClick={() => fetchProperties(1, {})} className="text-[#55C5D0] font-bold">Try again</button>
                        </div>
                    ) : filteredProperties.length === 0 && (loadingMore || nextPage) ? (
                        // A fetched page was entirely filtered out (e.g. all Sold comps)
                        // but more pages exist — show a loader while we auto-advance
                        // instead of a misleading "No properties found".
                        <div className="flex items-center justify-center py-24 gap-3">
                            <div className="animate-spin rounded-full h-8 w-8 border-2 border-[#55C5D0] border-t-transparent" />
                            <span className="text-sm text-gray-500">Loading properties...</span>
                        </div>
                    ) : filteredProperties.length === 0 ? (
                        <div className="flex flex-col items-center justify-center py-24 bg-white rounded-2xl border border-dashed border-gray-200">
                            <span className="text-5xl mb-4">🏠</span>
                            <span className="text-gray-900 text-xl font-bold mb-2">No properties found</span>
                            <p className="text-gray-400">Try adjusting your filters or search terms</p>
                        </div>
                    ) : viewMode === "grid" ? (
                        <PropertyGridView
                            properties={paginatedProperties}
                            getDaysOnMarket={getDaysOnMarket}
                            basePath="/collings-properties"
                            onEdit={handleEdit}
                        />

                    ) : (
                        <PropertyListView
                            properties={paginatedProperties}
                            handleSort={handleSort}
                            navigate={navigate}
                            capitalizeEachWord={capitalizeEachWord}
                            onEdit={handleEdit}
                            basePath="/collings-properties"
                            sortConfig={sortConfig}
                            onStageUpdate={handleStageUpdate}
                        />
                    )}
                </div>

                {totalCount > 0 && (
                    <div className="flex justify-center mt-6">
                        <span className="text-sm text-gray-500">
                            Showing {filteredProperties.length} of {totalCount} properties
                        </span>
                    </div>
                )}

                {/* Sentinel for infinite scroll: when this becomes visible, load next page.
                    Gated on nextPage only (not visible-row count) so a page that's fully
                    filtered out client-side doesn't dead-end pagination. */}
                {nextPage && !error && (
                    <div
                        ref={loadMoreSentinelRef}
                        className="h-10 flex items-center justify-center mt-6"
                        aria-hidden="true"
                    />
                )}

                {loadingMore && (
                    <div className="flex items-center justify-center py-6 gap-3">
                        <div className="animate-spin rounded-full h-8 w-8 border-2 border-[#55C5D0] border-t-transparent" />
                        <span className="text-sm text-gray-500">Loading more properties...</span>
                    </div>
                )}
            </div>




        </div >
    );
};

export default CollingsPropertiesPage;
